/* Maison Batu — Phase IV · production runtime
   Art-directed 12.40 s transition, wind and water choreography ported to
   section-local state. The runtime never writes state or CSS variables to
   <body>, supports more than one Phase-IV section, and contains no preview
   switches or URL hooks. */
(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-mb4-root]";
  const FRAME_QUERY = "(max-width: 1100px) and (orientation: portrait)";
  const START_RATIO = 0.40;
  const PAUSE_RATIO = 0.15;

  function boot(scope) {
    const roots = [];
    if (scope && scope.nodeType === 1 && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope && scope.querySelectorAll) roots.push(...scope.querySelectorAll(ROOT_SELECTOR));
    for (const root of new Set(roots)) {
      if (!root.__mb4Phase4Controller) initSection(root);
    }
  }

  function initSection(root) {
    const scene = root.querySelector("[data-mb4-window]");
    if (!scene) return;
    /* The mobile master is intentionally taller than the visible window and
       can be cropped by layout. Animation geometry must follow that source
       canvas, not the viewport-sized clipping window. */
    const art = root.querySelector("[data-mb4-art]") || scene;

    const $ = (selector) => root.querySelector(selector);
    const $$ = (selector) => Array.from(root.querySelectorAll(selector));
    const aborter = typeof AbortController === "function" ? new AbortController() : null;
    const listenerOptions = (options) => aborter ? Object.assign({}, options || {}, { signal: aborter.signal }) : options;
    const listen = (target, type, handler, options) => target.addEventListener(type, handler, listenerOptions(options));
    const clamp01 = (value) => value < 0 ? 0 : value > 1 ? 1 : value;
    const smooth = (value) => value * value * (3 - 2 * value);
    const segment = (time, from, to) => clamp01((time - from) / (to - from));
    const show = (element, visible) => {
      if (!element) return;
      const value = visible ? "visible" : "hidden";
      if (element.style.visibility !== value) element.style.visibility = value;
    };
    const sortedFx = (selector, datasetKeys) => $$(selector).sort((a, b) => {
      const value = (element) => {
        for (const key of datasetKeys) {
          const n = Number(element.dataset[key]);
          if (Number.isFinite(n)) return n;
        }
        return 0;
      };
      return value(a) - value(b);
    });

    const FX = {
      sun: $("[data-mb4-sun]"),
      sunglow: $("[data-mb4-sunglow]"),
      moon: $("[data-mb4-moon]"),
      mist: $("[data-mb4-mist]"),
      pavmist: $("[data-mb4-pavmist]"),
      daysky: $$(".mb4-daysky"),
      rings: sortedFx("[data-mb4-ripple]", ["mb4Ripple"]),
      petals: sortedFx("[data-mb4-petal]", ["mb4Petal"]),
    };

    /* One lightweight canvas adds the two atmospheric details that cannot be
       expressed by the supplied plates: the day heat shimmer over the right
       dunes and the broken silver moon path on the water. It is injected into
       the section so the Liquid markup, cards and waitlist stay untouched. */
    const atmosphereCanvas = document.createElement("canvas");
    atmosphereCanvas.className = "mb4-atmosphere";
    atmosphereCanvas.setAttribute("aria-hidden", "true");
    art.insertBefore(atmosphereCanvas, FX.sunglow || art.firstChild);
    const atmosphereContext = atmosphereCanvas.getContext("2d", { alpha: true });
    let atmosphereWidth = 0;
    let atmosphereHeight = 0;

    function sizeAtmosphere() {
      if (!atmosphereContext) return null;
      const rect = art.getBoundingClientRect();
      const width = Math.max(1, rect.width);
      const height = Math.max(1, rect.height);
      const dpr = Math.min(1.5, Math.max(1, window.devicePixelRatio || 1));
      const pixelWidth = Math.round(width * dpr);
      const pixelHeight = Math.round(height * dpr);
      if (atmosphereCanvas.width !== pixelWidth || atmosphereCanvas.height !== pixelHeight) {
        atmosphereCanvas.width = pixelWidth;
        atmosphereCanvas.height = pixelHeight;
      }
      atmosphereWidth = width;
      atmosphereHeight = height;
      atmosphereContext.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { width, height };
    }

    function drawAtmosphere(time) {
      const size = sizeAtmosphere();
      if (!size) return;
      const { width, height } = size;
      const context = atmosphereContext;
      context.clearRect(0, 0, width, height);

      const mobile = root.dataset.frame === "mobile";
      const heatIn = smooth(segment(time, 0.25, 1.15));
      const heatOut = 1 - smooth(segment(time, 4.30, 8.10));
      const heatOpacity = heatIn * heatOut;
      if (heatOpacity > 0.002) {
        context.save();
        context.globalCompositeOperation = "screen";
        context.filter = `blur(${Math.max(0.45, width * 0.00075).toFixed(2)}px)`;
        const startX = width * (mobile ? 0.56 : 0.66);
        const endX = width * 0.985;
        const topY = height * (mobile ? 0.365 : 0.405);
        const bandGap = height * 0.026;
        for (let band = 0; band < 7; band += 1) {
          const phase = time * (0.42 + band * 0.018) + band * 0.91;
          const y = topY + band * bandGap + Math.sin(phase) * height * 0.0035;
          const gradient = context.createLinearGradient(startX, y, endX, y);
          gradient.addColorStop(0, "rgba(255,205,144,0)");
          gradient.addColorStop(0.22, `rgba(255,215,166,${(0.030 * heatOpacity).toFixed(4)})`);
          gradient.addColorStop(0.70, `rgba(255,236,204,${(0.082 * heatOpacity).toFixed(4)})`);
          gradient.addColorStop(1, "rgba(255,225,180,0)");
          context.beginPath();
          context.moveTo(startX, y);
          const amplitude = height * (0.0042 + band * 0.00025);
          context.bezierCurveTo(
            startX + (endX - startX) * 0.30, y + Math.sin(phase + 0.8) * amplitude,
            startX + (endX - startX) * 0.68, y + Math.sin(phase + 2.0) * amplitude,
            endX, y + Math.sin(phase + 2.8) * amplitude
          );
          context.strokeStyle = gradient;
          context.lineWidth = Math.max(0.8, width * 0.0012);
          context.stroke();
        }
        context.restore();
      }

      const moonPath = smooth(segment(time, 8.60, 12.15));
      if (moonPath > 0.002) {
        context.save();
        context.globalCompositeOperation = "screen";
        const horizonY = height * (mobile ? 0.405 : 0.438);
        const bottomY = height * 0.965;
        const sourceX = width * (mobile ? 0.750 : 0.686);
        const landingX = width * (mobile ? 0.585 : 0.815);
        for (let pool = 0; pool < 9; pool += 1) {
          const progress = (pool + 1) / 10;
          const centreX = sourceX + (landingX - sourceX) * Math.pow(progress, 0.82);
          const centreY = horizonY + (bottomY - horizonY) * Math.pow(progress, 1.08);
          const radiusX = width * (0.008 + 0.105 * Math.pow(progress, 1.65));
          const radiusY = height * (0.0035 + 0.008 * progress);
          context.save();
          context.translate(centreX, centreY);
          context.scale(radiusX, radiusY);
          const poolGlow = context.createRadialGradient(0, 0, 0, 0, 0, 1);
          poolGlow.addColorStop(0, `rgba(196,228,246,${(0.070 * moonPath * (0.55 + 0.45 * progress)).toFixed(4)})`);
          poolGlow.addColorStop(0.42, `rgba(174,216,240,${(0.030 * moonPath).toFixed(4)})`);
          poolGlow.addColorStop(1, "rgba(160,205,234,0)");
          context.beginPath();
          context.arc(0, 0, 1, 0, Math.PI * 2);
          context.fillStyle = poolGlow;
          context.fill();
          context.restore();
        }
        const lines = mobile ? 28 : 36;
        for (let index = 0; index < lines; index += 1) {
          const progress = (index + 0.5) / lines;
          const spacing = (bottomY - horizonY) / lines;
          const y = horizonY + (bottomY - horizonY) * Math.pow(progress, 1.12)
            + Math.sin(index * 4.37 + time * 0.31) * spacing * 0.34;
          const centre = sourceX + (landingX - sourceX) * Math.pow(progress, 0.82);
          const half = width * (0.005 + 0.160 * Math.pow(progress, 1.65));
          const flicker = 0.58 + 0.42 * Math.sin(index * 2.17 + time * (1.15 + progress));
          const pieces = index % 4 === 0 ? 3 : 2;
          for (let piece = 0; piece < pieces; piece += 1) {
            const scatter = Math.sin(index * 8.71 + piece * 4.13 + time * 0.43);
            const offset = scatter * half * (0.35 + piece * 0.20);
            const span = half * (0.09 + 0.15 * (0.5 + 0.5 * Math.sin(index * 3.07 + piece * 2.61)));
            context.beginPath();
            context.moveTo(centre + offset - span, y);
            context.quadraticCurveTo(
              centre + offset,
              y + Math.sin(index + piece * 1.7 + time) * height * 0.0014,
              centre + offset + span,
              y
            );
            context.strokeStyle = `rgba(190,224,244,${(moonPath * (0.055 + 0.170 * progress) * flicker).toFixed(4)})`;
            context.lineWidth = Math.max(0.65, width * (0.00065 + progress * 0.00065));
            context.stroke();
          }
        }
        context.restore();
      }
    }

    if (!root.dataset.state) root.dataset.state = "day";
    if (!root.dataset.anim) root.dataset.anim = "idle";
    if (!root.dataset.drop) root.dataset.drop = "idle";
    if (!root.dataset.seq) root.dataset.seq = "idle";
    root.dataset.mb4Ready = "0";

    /* ───────────────────── Phase C: day to night ───────────────────── */

    const TIMELINE_END = 12.40;
    const SUN = Object.freeze({ from: 1.00, to: 6.85 });
    const DUSK = Object.freeze({ from: 2.20, to: 8.90 });
    const ZONE = Object.freeze({ from: 6.80, to: 11.40 });
    const MOON = Object.freeze({ from: 7.45, to: 11.85 });
    const ZONE_KEYS = ["z1", "z2", "z3", "z4", "z5"];
    const WATER_MIST_REST = 0.16;
    /* Keep the final ground mist legible against the blue night plate. At the
       former .16 it was technically present but disappeared perceptually once
       the dark plate, cards and pavilion highlights were all in place. */
    const MIST_REST = 0.34;

    let geometry = null;
    let geometryFrame = "";

    function readGeometry() {
      const frame = root.dataset.frame || "desktop";
      if (geometry && geometryFrame === frame) return geometry;
      const css = getComputedStyle(root);
      const number = (name, fallback) => {
        const value = parseFloat(css.getPropertyValue(name));
        return Number.isFinite(value) ? value : fallback;
      };
      const rect = art.getBoundingClientRect();
      geometry = {
        aspect: rect.height > 0 ? rect.width / rect.height : 1.78,
        sunX0: number("--mb4-sun-x0", frame === "mobile" ? 74.4 : 66.4),
        sunX1: number("--mb4-sun-x1", frame === "mobile" ? 75.8 : 67.7),
        sunY0: number("--mb4-sun-y0", frame === "mobile" ? 31.8 : 36.8),
        sunY1: number("--mb4-sun-y1", frame === "mobile" ? 40.5 : 44.8),
        sunW: number("--mb4-sun-w", frame === "mobile" ? 11.6 : 10.4) / 100,
        glowW: number("--mb4-sunglow-w", frame === "mobile" ? 68 : 60) / 100,
        moonX0: number("--mb4-moon-x0", frame === "mobile" ? 89.0 : 76.0),
        moonX1: number("--mb4-moon-x1", frame === "mobile" ? 83.0 : 68.6),
        moonY0: number("--mb4-moon-y0", frame === "mobile" ? 40.5 : 47.0),
        moonY1: number("--mb4-moon-y1", frame === "mobile" ? 9.6 : 19.8),
        moonW: number("--mb4-moon-w", frame === "mobile" ? 9.6 : 7.8) / 100,
      };
      geometryFrame = frame;
      return geometry;
    }

    /* CSS background-position aligns equal percentage points in image and
       box. This conversion places the luminous body's centre exactly at the
       measured scene coordinate. */
    const backgroundPosition = (centre, size) =>
      (size >= 1 ? centre : (centre - size / 2) / (1 - size)) * 100;

    function timelineValues(time) {
      const g = readGeometry();
      const sunProgress = segment(time, SUN.from, SUN.to);
      const sunCx = (g.sunX0 + (g.sunX1 - g.sunX0) * sunProgress) / 100;
      const sunCy = (g.sunY0 + (g.sunY1 - g.sunY0) * sunProgress) / 100;

      const dayskyIn = smooth(segment(time, 0.12, 0.95));
      const dayskyOut = 1 - smooth(segment(time, 4.60, 6.90));
      const daysky = dayskyIn * dayskyOut;
      const dayTone = smooth(segment(time, 2.00, 7.20));
      const duskProgress = smooth(segment(time, DUSK.from, DUSK.to));
      const duskA = -130 + 240 * duskProgress;
      const moonProgress = segment(time, MOON.from, MOON.to);
      const moonCx = (g.moonX0 + (g.moonX1 - g.moonX0) * Math.pow(moonProgress, 1.55)) / 100;
      const moonCy = (g.moonY0 + (g.moonY1 - g.moonY0) * Math.pow(moonProgress, 0.70)) / 100;
      const mistRise = smooth(segment(time, 2.20, 7.45));
      const mistFall = smooth(segment(time, 7.85, 11.75));
      const moonOpacity = smooth(segment(time, 7.85, 10.05));
      const moonFocus = smooth(segment(time, 8.05, 10.25));
      const sunWarmth = smooth(segment(time, 3.00, 6.35));

      return {
        sunCx: sunCx * 100,
        sunCy: sunCy * 100,
        sunBx: backgroundPosition(sunCx, g.sunW),
        sunBy: backgroundPosition(sunCy, g.sunW * g.aspect),
        glowBx: backgroundPosition(sunCx, g.glowW),
        glowBy: backgroundPosition(sunCy, g.glowW * g.aspect),
        daysky,
        dayBrightness: 1 - 0.42 * dayTone,
        daySaturation: 1 + 0.28 * dayTone,
        daySepia: 0.16 * dayTone,
        dayHue: -7 * dayTone,
        sunOpacity: dayskyIn * (1 - 0.18 * sunWarmth),
        sunCoreOpacity: 0.96 - 0.24 * sunWarmth,
        sunScaleY: 1 - 0.16 * sunWarmth,
        glowOpacity: dayskyIn * (1 - smooth(segment(time, 2.70, 6.10))),
        duskA,
        duskB: duskA + 130,
        moonBx: backgroundPosition(moonCx, g.moonW),
        moonBy: backgroundPosition(moonCy, g.moonW * g.aspect),
        moonOpacity,
        moonBlur: (1 - moonFocus) * 2.8,
        moonSepia: (1 - moonFocus) * 0.42,
        moonSaturation: 0.82 + 0.18 * moonFocus,
        zone: smooth(segment(time, ZONE.from, ZONE.to)),
        pavmist: 0.82 * mistRise - (0.82 - MIST_REST) * mistFall,
      };
    }

    function applyTimeline(time) {
      const values = timelineValues(time);
      const style = root.style;

      show(FX.sun, values.sunOpacity > 0.002 && time < SUN.to + 0.3);
      show(FX.sunglow, values.glowOpacity > 0.002);
      show(FX.moon, values.moonOpacity > 0.002);
      show(FX.pavmist, values.pavmist > 0.004);

      for (const element of FX.daysky) {
        if (values.daysky > 0.002) element.setAttribute("data-on", "");
        else element.removeAttribute("data-on");
      }

      style.setProperty("--mb4-sun-bx", `${values.sunBx.toFixed(3)}%`);
      style.setProperty("--mb4-sun-by", `${values.sunBy.toFixed(3)}%`);
      style.setProperty("--mb4-sun-cx", `${values.sunCx.toFixed(3)}%`);
      style.setProperty("--mb4-sun-cy", `${values.sunCy.toFixed(3)}%`);
      style.setProperty("--mb4-sun-o", values.sunOpacity.toFixed(4));
      style.setProperty("--mb4-sun-core-o", values.sunCoreOpacity.toFixed(4));
      style.setProperty("--mb4-sun-scale-y", values.sunScaleY.toFixed(4));
      style.setProperty("--mb4-sunglow-bx", `${values.glowBx.toFixed(3)}%`);
      style.setProperty("--mb4-sunglow-by", `${values.glowBy.toFixed(3)}%`);
      style.setProperty("--mb4-sunglow-o", values.glowOpacity.toFixed(4));
      style.setProperty("--mb4-daysky-o", values.daysky.toFixed(4));
      style.setProperty("--mb4-day-brightness", values.dayBrightness.toFixed(4));
      style.setProperty("--mb4-day-saturation", values.daySaturation.toFixed(4));
      style.setProperty("--mb4-day-sepia", values.daySepia.toFixed(4));
      style.setProperty("--mb4-day-hue", `${values.dayHue.toFixed(3)}deg`);
      style.setProperty("--mb4-moon-bx", `${values.moonBx.toFixed(3)}%`);
      style.setProperty("--mb4-moon-by", `${values.moonBy.toFixed(3)}%`);
      style.setProperty("--mb4-moon-o", values.moonOpacity.toFixed(4));
      style.setProperty("--mb4-moon-blur", `${values.moonBlur.toFixed(3)}px`);
      style.setProperty("--mb4-moon-sepia", values.moonSepia.toFixed(4));
      style.setProperty("--mb4-moon-saturation", values.moonSaturation.toFixed(4));
      style.setProperty("--mb4-dusk-a", `${values.duskA.toFixed(2)}%`);
      style.setProperty("--mb4-dusk-b", `${values.duskB.toFixed(2)}%`);
      for (const key of ZONE_KEYS) style.setProperty(`--mb4-${key}`, values.zone.toFixed(4));
      style.setProperty("--mb4-pavmist-o", values.pavmist.toFixed(4));
    }

    function pinStaticEnd() {
      const end = timelineValues(TIMELINE_END);
      root.style.setProperty("--mb4-moon-bx-end", `${end.moonBx.toFixed(3)}%`);
      root.style.setProperty("--mb4-moon-by-end", `${end.moonBy.toFixed(3)}%`);
      root.style.setProperty("--mb4-pavmist-rest", MIST_REST.toFixed(4));
    }

    /* ─────────────────── Phase D: wind and water ───────────────────── */

    const DROP_END = 8.80;
    const RING_LIFE = 1.60;
    const PETAL_GEOMETRY = Object.freeze({
      desktop: Object.freeze([
        { x0: 6,  y0: 4,  x1: 28, y1: 71, t: 0.00, dur: 4.15, spin: 220,  sway: 4.2 },
        { x0: 14, y0: 9,  x1: 50, y1: 64, t: 0.20, dur: 4.55, spin: -190, sway: 5.1 },
        { x0: 3,  y0: 15, x1: 74, y1: 76, t: 0.42, dur: 4.05, spin: 250,  sway: 4.0 },
        { x0: 22, y0: 6,  x1: 84, y1: 60, t: 0.64, dur: 4.70, spin: -230, sway: 5.3 },
        { x0: 10, y0: 21, x1: 39, y1: 74, t: 0.86, dur: 4.30, spin: 180,  sway: 4.4 },
        { x0: 28, y0: 13, x1: 88, y1: 67, t: 1.08, dur: 4.65, spin: -250, sway: 5.8 },
        { x0: 17, y0: 26, x1: 58, y1: 72, t: 1.30, dur: 4.40, spin: 210,  sway: 4.7 },
        { x0: 8,  y0: 11, x1: 34, y1: 65, t: 1.52, dur: 4.75, spin: -200, sway: 6.0 },
        { x0: 25, y0: 20, x1: 78, y1: 70, t: 1.74, dur: 4.35, spin: 260,  sway: 4.5 },
        { x0: 13, y0: 30, x1: 54, y1: 77, t: 1.96, dur: 4.20, spin: -220, sway: 4.9 },
        { x0: 31, y0: 9,  x1: 42, y1: 62, t: 2.18, dur: 4.65, spin: 195,  sway: 6.2 },
        { x0: 5,  y0: 24, x1: 36, y1: 75, t: 2.40, dur: 4.25, spin: -240, sway: 4.3 },
        { x0: 20, y0: 33, x1: 90, y1: 78, t: 2.62, dur: 4.10, spin: 230,  sway: 5.4 },
        { x0: 34, y0: 17, x1: 72, y1: 68, t: 2.82, dur: 4.35, spin: -210, sway: 5.7 },
      ]),
      mobile: Object.freeze([
        { x0: 5,  y0: 16, x1: 27, y1: 72, t: 0.00, dur: 4.25, spin: 220,  sway: 6.0 },
        { x0: 14, y0: 21, x1: 49, y1: 66, t: 0.20, dur: 4.60, spin: -190, sway: 7.2 },
        { x0: 2,  y0: 26, x1: 70, y1: 77, t: 0.42, dur: 4.10, spin: 250,  sway: 5.5 },
        { x0: 19, y0: 18, x1: 81, y1: 63, t: 0.64, dur: 4.75, spin: -230, sway: 7.0 },
        { x0: 9,  y0: 30, x1: 38, y1: 75, t: 0.86, dur: 4.35, spin: 180,  sway: 6.1 },
        { x0: 23, y0: 24, x1: 76, y1: 69, t: 1.08, dur: 4.70, spin: -250, sway: 8.0 },
        { x0: 12, y0: 33, x1: 57, y1: 78, t: 1.30, dur: 4.45, spin: 210,  sway: 6.6 },
        { x0: 3,  y0: 22, x1: 32, y1: 70, t: 1.52, dur: 4.80, spin: -200, sway: 7.4 },
        { x0: 21, y0: 29, x1: 74, y1: 74, t: 1.74, dur: 4.40, spin: 260,  sway: 6.3 },
        { x0: 7,  y0: 19, x1: 53, y1: 67, t: 1.96, dur: 4.30, spin: -220, sway: 7.0 },
        { x0: 17, y0: 35, x1: 41, y1: 79, t: 2.18, dur: 4.70, spin: 195,  sway: 8.1 },
        { x0: 1,  y0: 28, x1: 35, y1: 76, t: 2.40, dur: 4.30, spin: -240, sway: 5.8 },
        { x0: 25, y0: 20, x1: 78, y1: 71, t: 2.62, dur: 4.55, spin: 230,  sway: 7.5 },
        { x0: 15, y0: 25, x1: 62, y1: 73, t: 2.82, dur: 4.35, spin: -210, sway: 6.4 },
      ]),
    });

    const petalGeometry = () => PETAL_GEOMETRY[root.dataset.frame] || PETAL_GEOMETRY.desktop;
    const easeOut = (value) => 1 - Math.pow(1 - value, 3);
    let ringEvents = [];
    let ringEventsFrame = "";

    function scheduleRings() {
      const events = petalGeometry()
        .map((petal, index) => ({ t: petal.t + petal.dur, x: petal.x1, y: petal.y1, index }))
        .sort((a, b) => a.t - b.t);
      const freeAt = FX.rings.map(() => -99);
      for (const event of events) {
        if (!freeAt.length) { event.ring = -1; continue; }
        let ring = 0;
        for (let index = 1; index < freeAt.length; index += 1) {
          if (freeAt[index] < freeAt[ring]) ring = index;
        }
        event.ring = ring;
        freeAt[ring] = event.t + RING_LIFE;
      }
      ringEvents = events;
      ringEventsFrame = root.dataset.frame;
    }

    function applyDrop(time) {
      if (ringEventsFrame !== root.dataset.frame) scheduleRings();
      const petals = petalGeometry();

      petals.forEach((petal, index) => {
        const element = FX.petals[index];
        if (!element) return;
        const progress = clamp01((time - petal.t) / petal.dur);
        const live = time >= petal.t && time < petal.t + petal.dur + 0.12;
        show(element, live);
        if (!live) {
        element.style.setProperty("--mb4-o", "0");
          return;
        }
        const drift = progress * progress * 0.42 + progress * 0.58;
        const x = petal.x0 + (petal.x1 - petal.x0) * drift
          + Math.sin(progress * Math.PI * 4) * petal.sway * Math.sin(progress * Math.PI);
        const y = petal.y0 + (petal.y1 - petal.y0)
          * (progress * 0.86 + progress * progress * 0.14);
        const opacity = progress < 0.06
          ? progress / 0.06
          : progress > 0.94 ? (1 - progress) / 0.06 : 1;
        element.style.left = `${x.toFixed(3)}%`;
        element.style.top = `${y.toFixed(3)}%`;
        element.style.setProperty("--mb4-r", `${(petal.spin * progress).toFixed(1)}deg`);
        element.style.setProperty("--mb4-sx", Math.cos(progress * Math.PI * 3.1).toFixed(3));
        element.style.setProperty("--mb4-o", opacity.toFixed(4));
      });

      const ringState = FX.rings.map(() => null);
      for (const event of ringEvents) {
        if (event.ring < 0) continue;
        const progress = (time - event.t) / RING_LIFE;
        if (progress >= 0 && progress < 1) ringState[event.ring] = { progress, event };
      }
      FX.rings.forEach((element, index) => {
        const state = ringState[index];
        show(element, Boolean(state));
        if (!state) {
          element.style.setProperty("--mb4-o", "0");
          return;
        }
        const { progress, event } = state;
        const peak = 0.34;
        const opacity = progress < 0.10
          ? (progress / 0.10) * peak
          : peak * Math.pow(1 - (progress - 0.10) / 0.90, 1.5);
        element.style.width = "var(--mb4-ring-petal)";
        element.style.left = `${event.x.toFixed(3)}%`;
        element.style.top = `${event.y.toFixed(3)}%`;
        element.style.setProperty("--mb4-s", (0.06 + 0.94 * easeOut(progress)).toFixed(4));
        element.style.setProperty("--mb4-o", opacity.toFixed(4));
      });

      const mistProgress = clamp01((time - 1.10) / 6.10);
      const mistOpacity = mistProgress <= 0
        ? 0
        : mistProgress >= 1
          ? WATER_MIST_REST
          : Math.sin(Math.PI * mistProgress) * 0.52
            + WATER_MIST_REST * smooth(segment(mistProgress, 0.70, 1));
      show(FX.mist, mistOpacity > 0.002);
      root.style.setProperty("--mb4-mist-o", mistOpacity.toFixed(4));
      root.style.setProperty("--mb4-mist-x", `${(-16 + 32 * mistProgress).toFixed(2)}px`);
    }

    /* ─────────────────── Phase E: one shared clock ─────────────────── */

    const DROP_AT = 3.60;
    const SEQUENCE_END = Math.max(TIMELINE_END, DROP_AT + DROP_END);
    let raf = 0;
    let startedAt = 0;
    let elapsed = 0;
    let renderedTime = 0;
    let started = false;
    let running = false;
    let finished = false;
    let destroyed = false;
    const pauseReasons = new Set();

    function render(time) {
      renderedTime = Math.max(0, Math.min(SEQUENCE_END, time));
      applyTimeline(Math.min(renderedTime, TIMELINE_END));
      applyDrop(Math.min(renderedTime - DROP_AT, DROP_END));
      drawAtmosphere(renderedTime);
      scene.dataset.mb4Time = renderedTime.toFixed(3);
    }

    function cancelFrame() {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    }

    function markRunning() {
      root.dataset.state = "day";
      root.dataset.seq = "running";
      root.dataset.anim = "seq";
      root.dataset.drop = "seq";
    }

    function markFinished() {
      root.dataset.state = "night-on";
      root.dataset.seq = "done";
      root.dataset.anim = "done";
      root.dataset.drop = "done";
    }

    function frame(now) {
      if (!running || destroyed) return;
      elapsed = (now - startedAt) / 1000;
      if (elapsed >= SEQUENCE_END) {
        elapsed = SEQUENCE_END;
        render(SEQUENCE_END);
        running = false;
        finished = true;
        raf = 0;
        markFinished();
        root.dispatchEvent(new CustomEvent("mb4:complete", { bubbles: false }));
        return;
      }
      render(elapsed);
      raf = requestAnimationFrame(frame);
    }

    function startFrame() {
      if (running || finished || destroyed || pauseReasons.size) return;
      startedAt = performance.now() - elapsed * 1000;
      running = true;
      raf = requestAnimationFrame(frame);
    }

    function play() {
      if (destroyed || running || finished) return;
      started = true;
      elapsed = 0;
      markRunning();
      render(0);
      startFrame();
    }

    function pause(reason) {
      pauseReasons.add(reason);
      if (!running) return;
      cancelFrame();
      running = false;
    }

    function resume(reason) {
      pauseReasons.delete(reason);
      if (started && !finished && root.dataset.seq === "running") startFrame();
    }

    function finishWithoutMotion() {
      cancelFrame();
      started = true;
      running = false;
      finished = true;
      elapsed = SEQUENCE_END;
      markFinished();
      render(SEQUENCE_END);
    }

    function reset() {
      cancelFrame();
      started = false;
      running = false;
      finished = false;
      elapsed = 0;
      renderedTime = 0;
      root.dataset.state = "day";
      root.dataset.seq = "idle";
      root.dataset.anim = "idle";
      root.dataset.drop = "idle";
      const variables = [
        "--mb4-sun-bx", "--mb4-sun-by", "--mb4-sun-cx", "--mb4-sun-cy", "--mb4-sun-o", "--mb4-sun-core-o", "--mb4-sun-scale-y", "--mb4-sunglow-bx", "--mb4-sunglow-by",
        "--mb4-sunglow-o", "--mb4-daysky-o", "--mb4-day-brightness", "--mb4-day-saturation", "--mb4-day-sepia", "--mb4-day-hue", "--mb4-moon-bx", "--mb4-moon-by", "--mb4-moon-o",
        "--mb4-moon-blur", "--mb4-moon-sepia", "--mb4-moon-saturation",
        "--mb4-dusk-a", "--mb4-dusk-b", "--mb4-pavmist-o", "--mb4-mist-o", "--mb4-mist-x",
        ...ZONE_KEYS.map((key) => `--mb4-${key}`),
      ];
      for (const variable of variables) root.style.removeProperty(variable);
      for (const element of FX.daysky) element.removeAttribute("data-on");
      for (const element of [FX.sun, FX.sunglow, FX.moon, FX.mist, FX.pavmist, ...FX.rings, ...FX.petals]) {
        if (!element) continue;
        element.style.removeProperty("visibility");
        element.style.removeProperty("left");
        element.style.removeProperty("top");
        element.style.removeProperty("width");
        for (const variable of ["--mb4-o", "--mb4-r", "--mb4-sx", "--mb4-s"]) element.style.removeProperty(variable);
      }
      if (atmosphereContext) atmosphereContext.clearRect(0, 0, atmosphereWidth, atmosphereHeight);
      scene.dataset.mb4Time = "0.000";
    }

    /* ───────────────── Art direction and decode gate ──────────────── */

    const frameMedia = window.matchMedia(FRAME_QUERY);
    let decodeGeneration = 0;
    let assetsReady = false;
    let startEligible = false;
    let startObserver = null;
    let visibilityObserver = null;

    function imagePromise(image) {
      if (!image) return Promise.resolve();
      if (typeof image.decode === "function") return image.decode().catch(() => undefined);
      if (image.complete) return Promise.resolve();
      return new Promise((resolve) => {
        image.addEventListener("load", resolve, { once: true });
        image.addEventListener("error", resolve, { once: true });
      });
    }

    function backgroundUrls(elements) {
      const urls = new Set();
      const pattern = /url\((?:"([^"]+)"|'([^']+)'|([^)]*))\)/g;
      for (const element of elements) {
        if (!element) continue;
        const background = getComputedStyle(element).backgroundImage || "";
        let match;
        while ((match = pattern.exec(background))) {
          const url = (match[1] || match[2] || match[3] || "").trim();
          if (url && url !== "none") urls.add(url);
        }
      }
      return [...urls];
    }

    function preloadUrl(url) {
      return new Promise((resolve) => {
        const image = new Image();
        image.onload = resolve;
        image.onerror = resolve;
        image.src = url;
        if (typeof image.decode === "function") image.decode().then(resolve, resolve);
      });
    }

    function decodeActiveFrame() {
      const generation = ++decodeGeneration;
      assetsReady = false;
      root.dataset.mb4Ready = "0";
      pause("decode");

      const frame = root.dataset.frame || "desktop";
      const host = root.querySelector(`[data-mb4-frame="${frame}"]`);
      const images = new Set(host ? Array.from(host.querySelectorAll("img")) : []);
      for (const image of $$("img[data-mb4-critical], img[data-mb4-mist], img[data-mb4-pavmist], img[data-mb4-ripple], img[data-mb4-petal]")) {
        images.add(image);
      }
      const backgrounds = backgroundUrls([FX.sun, FX.sunglow, FX.moon]);

      return Promise.all([
        ...Array.from(images, imagePromise),
        ...backgrounds.map(preloadUrl),
      ]).then(() => {
        if (destroyed || generation !== decodeGeneration) return;
        assetsReady = true;
        root.dataset.mb4Ready = "1";
        resume("decode");
        maybeStart();
        root.dispatchEvent(new CustomEvent("mb4:ready", { bubbles: false }));
      });
    }

    function syncFrame() {
      const next = frameMedia.matches ? "mobile" : "desktop";
      if (root.dataset.frame === next) return;
      root.dataset.frame = next;
      geometry = null;
      geometryFrame = "";
      ringEventsFrame = "";
      pinStaticEnd();
      if (started || finished) render(finished ? SEQUENCE_END : renderedTime);
      decodeActiveFrame();
    }

    const designMode = Boolean(window.Shopify && window.Shopify.designMode);
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const respectReducedMotion = root.dataset.respectReducedMotion !== "false";
    const staticMode = designMode || (respectReducedMotion && reducedMotion) || root.dataset.mb4Autoplay === "0";

    function maybeStart() {
      if (destroyed || staticMode || started || !assetsReady || !startEligible) return;
      play();
      if (startObserver) {
        startObserver.disconnect();
        startObserver = null;
      }
    }

    /* Set the frame before geometry is sampled. */
    root.dataset.frame = frameMedia.matches ? "mobile" : "desktop";
    pinStaticEnd();
    scheduleRings();

    if (typeof frameMedia.addEventListener === "function") {
      listen(frameMedia, "change", syncFrame);
    } else if (typeof frameMedia.addListener === "function") {
      frameMedia.addListener(syncFrame);
    }
    listen(window, "resize", () => {
      geometry = null;
      geometryFrame = "";
      pinStaticEnd();
      if (started || finished) render(finished ? SEQUENCE_END : renderedTime);
    }, { passive: true });

    if (document.hidden) pauseReasons.add("document");
    listen(document, "visibilitychange", () => {
      if (document.hidden) pause("document");
      else resume("document");
    });

    if ("IntersectionObserver" in window) {
      visibilityObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio < PAUSE_RATIO) pause("viewport");
          else resume("viewport");
        }
      }, { threshold: [0, PAUSE_RATIO, 0.5] });
      visibilityObserver.observe(scene);

      if (!staticMode) {
        startObserver = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && entry.intersectionRatio >= START_RATIO) {
              startEligible = true;
              maybeStart();
            }
          }
        }, { threshold: [0, START_RATIO] });
        startObserver.observe(scene);
      }
    } else {
      startEligible = true;
    }

    if (staticMode) finishWithoutMotion();
    decodeActiveFrame();

    /* ───────────────── Customer-form return position ───────────────── */

    const waitlistKey = `maisonBatuRefractionWaitlistScroll:${root.id || window.location.pathname}`;
    for (const form of $$("form[data-mb4-waitlist-form], form.mb4-waitlist__form")) {
      listen(form, "submit", () => {
        try {
          sessionStorage.setItem(waitlistKey, JSON.stringify({
            path: window.location.pathname,
            section: root.id || "",
            y: window.scrollY,
          }));
        } catch (_) {}
      });
    }

    function restoreWaitlistPosition() {
      const params = new URLSearchParams(window.location.search);
      if (!params.has("customer_posted") && !window.location.href.includes("customer_posted=true")) return;
      try {
        const stored = JSON.parse(sessionStorage.getItem(waitlistKey) || "{}");
        if (stored.path !== window.location.pathname || stored.section !== (root.id || "") || typeof stored.y !== "number") return;
        if (window.location.hash) {
          history.replaceState(history.state, "", `${window.location.pathname}${window.location.search}`);
        }
        requestAnimationFrame(() => requestAnimationFrame(() => {
          window.scrollTo({ top: stored.y, behavior: "auto" });
          sessionStorage.removeItem(waitlistKey);
        }));
      } catch (_) {
        try { sessionStorage.removeItem(waitlistKey); } catch (__) {}
      }
    }
    restoreWaitlistPosition();

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      decodeGeneration += 1;
      cancelFrame();
      if (startObserver) startObserver.disconnect();
      if (visibilityObserver) visibilityObserver.disconnect();
      if (typeof frameMedia.removeListener === "function" && typeof frameMedia.removeEventListener !== "function") {
        frameMedia.removeListener(syncFrame);
      }
      if (aborter) aborter.abort();
      atmosphereCanvas.remove();
      delete root.__mb4Phase4Controller;
    }

    listen(document, "shopify:section:unload", (event) => {
      if (event.target === root || (event.target && event.target.contains && event.target.contains(root))) destroy();
    });

    root.__mb4Phase4Controller = Object.freeze({
      duration: SEQUENCE_END,
      dropAt: DROP_AT,
      play,
      render: (time) => {
        started = true;
        finished = Number(time) >= SEQUENCE_END;
        if (finished) markFinished();
        else markRunning();
        render(Number(time) || 0);
      },
      reset,
      destroy,
    });
  }

  const start = () => boot(document);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();

  /* Shopify's theme editor replaces sections without a page reload. */
  document.addEventListener("shopify:section:load", (event) => boot(event.target));
})();
